import {
  ChevronDownIcon,
  SearchIcon,
  XIcon,
  AdjustmentsIcon,
} from "@heroicons/react/outline";
import { useRouter } from "next/router";
import { Listbox, Transition, RadioGroup } from "@headlessui/react";
import * as Text from "@/components/text";
import classNames from "classnames";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { usePrevious } from "@uidotdev/usehooks";
import Button from "../actions/Button";
import PropTypes from "prop-types";
import { FilterContext } from "stores/FilterContextProvider";
import { groupDictionary } from "@/constants/groupDictionary";
import { CheckBox } from "@/components/forms/Filter";
import ClientOnlyPortal from "@/components/lib/ClientOnlyPortal";
import { localeTexts} from "@/utils/locale";


export default function Filter() {
  const router = useRouter();
  const query = router.query;

  const [open, setOpen] = useState(false);
  const [filterType, setFilterType] = useState("todas-las-iniciativas");
  const [searchList, setSearchList] = useState([]);

  const [selected, setSelected] = useState([]);
  const previousSelection = usePrevious(selected);

  const filters = useContext(FilterContext);
  const searchRef = useRef(null);
  const [currentOpen, setCurrentOpen] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchFilterCollection, setSearchFilterCollection] = useState({});

  const [locale, setLocale] = useState("es-MX");
  const t = localeTexts[locale]?.common || {};
  // const commonT = localeTexts[locale]?.common || {};

    useEffect(() => {
    if (!router.isReady) return;
    const urlLocale = router.query.locale;
    const storedLocale = typeof window !== "undefined"
      ? localStorage.getItem("amb-locale")
      : null;
    const finalLocale = urlLocale || storedLocale || "es-MX";
    setLocale(finalLocale);
    localStorage.setItem("amb-locale", finalLocale);
  }, [router.isReady, router.query.locale]);
  
  const filterTypes = useMemo(() => ({
    [t.year || "Año"]: "Years",
    [t.country || "País"]: "Pais",
    [t.tourEditions || "Ediciones Gira"]: "EGIRA",
    [t.sections || "Secciones"]: "Secciones",
    [t.generations || "Generaciones"]: "Generaciones",
    [t.apEditions || "Ediciones AP"]: "EAP",
    [t.productionYear || "Año de producción"]: "Years",
  }), [t]);


  const initiativesMap = useMemo(() => [
      {
        name: t.allPrograms || "Todos los programas",
        slug: "todas-las-iniciativas",
      },
      {
        name: t.ambulantePresenta || "Ambulante Presenta", 
        slug: "ambulante-presenta",
      },
      {
        name: t.documentaryTour || "Gira de Documentales",
        slug: "gira-ambulante",
      },
      {
        name: t.ambulanteBeyond || "Ambulante Más Allá",
        slug: "ambulante-mas-alla",
      },
    ], [t]);



  // Configuration for theme name prefixes

  const excludedPrefixes = {
  'es-MX': ['mex', 'línea'],
  'en-US': ['mex', 'line']
  };

  // Sync locale with URL and localStorage

  useEffect(() => {
  if (!router.isReady) return;
  const urlLocale = router.query.locale;
  const storedLocale = typeof window !== "undefined"
  ? localStorage.getItem("amb-locale")
  : null;

  const finalLocale = urlLocale || storedLocale || "es-MX";
  setLocale(finalLocale);
  localStorage.setItem("amb-locale", finalLocale);
  }, [router.isReady, router.query.locale]);


    useEffect(() => {
      if (query?.iniciativa) {
        setFilterType(query.iniciativa);
      }
    }, [query.iniciativa]);

  useEffect(() => {
    selected.forEach((item) => {
      if (!Object.keys(query).includes(item.category.toLocaleLowerCase())) {
        // remove item from selected
        setSelected(selected.filter((i) => i.name !== item.name));
      }
    });
  }, [query]);

  useEffect(() => {
    if (selected.length > 0) {
      let filter = {};
      // create filter object
      selected.forEach((item) => {
        if (filter[item.category.toLocaleLowerCase()]) {
          filter[item.category.toLocaleLowerCase()].push(item.name);
        } else {
          filter[item.category.toLocaleLowerCase()] = [item.name];
        }
      });

      router.push({
        pathname: "/catalogo",
        query: {
          ...router.query,
          ...filter,
        },
      });
    } else {
      if (previousSelection?.length > 0) {
        router.push({
          pathname: "/catalogo",
        });
      }
    }
  }, [selected]);

  useEffect(() => {
    setIsSearching(false);
    setSearchList([]);
    setSearchFilterCollection({});
  }, [open]);

  // this list should only be grouped once
  const groupByCategory = useMemo(() => {
    const res = filters.reduce((acc, item) => {
      const { category } = item;
      // if no item in category create empty array
      if (!acc[category]) {
        acc[category] = [];
      }
      // if category exists push item
      acc[category].push(item);
      return acc;
    }, {});
    return res;
  }, [filters]);

  // const filterCollection = {
  //   "todas-las-iniciativas": {
  //     Secciones: groupByCategory["Secciones"],
  //     "Año de producción": groupByCategory["Years"],
  //     País: groupByCategory["Pais"],
  //   },
  //   "gira-ambulante": {
  //     "Ediciones Gira": groupByCategory["EGIRA"],
  //     Secciones: groupByCategory["Secciones"],
  //     "Año de producción": groupByCategory["Years"],
  //     País: groupByCategory["Pais"],
  //   },
  //   "ambulante-mas-alla": {
  //     Generación: groupByCategory["Generaciones"],
  //     "Año de producción": groupByCategory["Years"],
  //   },
  //   "ambulante-presenta": {
  //     "Ediciones AP": groupByCategory["EAP"],
  //     "Año de producción": groupByCategory["Years"],
  //     País: groupByCategory["Pais"],
  //   },
  // };

  useEffect(() => {
    if (isSearching) {
      const res = searchList.reduce((acc, item) => {
        const { category } = item;
        // if no item in category create empty array
        if (!acc[category]) {
          acc[category] = [];
        }
        // if category exists push item
        acc[category].push(item);
        return acc;
      }, {});
      const collection = {
        filters: {
          Año: res["Years"],
          País: res["Pais"],
        },
        initiatives: {
          Secciones: res["Secciones"],
          Año: res["Years"],
          País: res["Pais"],
        },
      };
      setSearchFilterCollection(collection);
    }
  }, [searchList, isSearching]);

  const handleSearch = async (eventCall) => {
    if (
      eventCall == "" ||
      eventCall.target.value === "" ||
      searchRef === null
    ) {
      setIsSearching(false);
      setSearchFilterCollection([]);
      setSearchList([]);
      return;
    }
    setIsSearching(true);
    let temp = [];

if (open && currentOpen !== "") {
  // Get the backend category identifier using translated label
  const categoryKey = Object.keys(filterTypes).find(
    key => filterTypes[key] === groupDictionary[currentOpen]
  );
  
    temp = groupByCategory[filterTypes[categoryKey]].filter((f) =>
      f.name.toString().toLocaleLowerCase()
        .includes(eventCall.target.value.toLocaleLowerCase())
    );
    setSearchList(temp);
    return;
  }
    if (eventCall.target.getAttribute("data-filtertype") == "All") {
      temp = filters.filter((f) => {
        return f.name
          .toString()
          .toLocaleLowerCase()
          .includes(eventCall.target.value.toLocaleLowerCase());
      });
    } else {
      temp = filters.filter((f) => {
        return (
          f.name
            .toString()
            .toLocaleLowerCase()
            .includes(eventCall.target.value.toLocaleLowerCase()) &&
          f.category == eventCall.target.getAttribute("data-filtername")
        );
      });
    }
    setSearchList(temp);
  };

  const filterCollection = useMemo(() => {
  // Get translated category labels
  const categories = {
    sections: t.sections || "Secciones",
    productionYear: t.productionYear || "Año de producción",
    country: t.country || "País",
    tourEditions: t.tourEditions || "Ediciones Gira",
    generations: t.generations || "Generación",
    apEditions: t.apEditions || "Ediciones AP",
  };

  // Map to your backend's category identifiers
  const categoryMap = {
    [categories.sections]: "Secciones",
    [categories.productionYear]: "Years",
    [categories.country]: "Pais",
    [categories.tourEditions]: "EGIRA",
    [categories.generations]: "Generaciones",
    [categories.apEditions]: "EAP",
  };

  return {
    "todas-las-iniciativas": {
      [categories.sections]: groupByCategory[categoryMap[categories.sections]],
      [categories.productionYear]: groupByCategory[categoryMap[categories.productionYear]],
      [categories.country]: groupByCategory[categoryMap[categories.country]],
    },
    "gira-ambulante": {
      [categories.tourEditions]: groupByCategory[categoryMap[categories.tourEditions]],
      [categories.sections]: groupByCategory[categoryMap[categories.sections]],
      [categories.productionYear]: groupByCategory[categoryMap[categories.productionYear]],
      [categories.country]: groupByCategory[categoryMap[categories.country]],
    },
    "ambulante-mas-alla": {
      [categories.generations]: groupByCategory[categoryMap[categories.generations]],
      [categories.productionYear]: groupByCategory[categoryMap[categories.productionYear]],
    },
    "ambulante-presenta": {
      [categories.apEditions]: groupByCategory[categoryMap[categories.apEditions]],
      [categories.productionYear]: groupByCategory[categoryMap[categories.productionYear]],
      [categories.country]: groupByCategory[categoryMap[categories.country]],
    },
  };
}, [t, groupByCategory]);

  // Desktop case

  return (
    <>
      {Object.entries(filterCollection[filterType]).map(([s, x], index) => {
        const filterLabel = Object.keys(filterTypes).find(key => 
            filterTypes[key] === groupDictionary[s]
          );
          const translatedLabel = t[filterLabel] || s;
        return (
          <Listbox
            key={index}
            as="div"
            className=" relative"
            value={selected}
            onChange={setSelected}
            multiple
          >
            {({ open }) => (
              <>
                <Listbox.Button
                  as={Button}
                  color={open ? "white-on-black-no-hover" : "primary-light"}
                  label={translatedLabel}
                  className=" px-4 font-semibold"
                  trailingIcon={<ChevronDownIcon width={32} height={32} />}
                  onClick={() => handleSearch("")}
                ></Listbox.Button>

                <Transition
                  enter="transition duration-100 ease-out"
                  enterFrom="transform scale-95 opacity-0"
                  enterTo="transform scale-100 opacity-100"
                  leave="transition duration-75 ease-out"
                  leaveFrom="transform scale-100 opacity-100"
                  leaveTo="transform scale-95 opacity-0"
                >
                  <Listbox.Options
                    className={classNames(
                      index ===
                        Object.entries(filterCollection[filterType]).length - 1
                        ? "right-0"
                        : "left-0",
                      "top -10 absolute z-50  flex h-[400px] w-[254px]  flex-col overflow-y-auto border-[1px] border-black bg-white"
                    )}
                  >
                    <Listbox.Option>
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full border-b-2 py-2 "
                      >
                        <div className="mx-2 flex items-center gap-4">
                          <SearchIcon
                            onClick={(e) => e.stopPropagation()}
                            className="text-neutral-500"
                            width={20}
                            height={20}
                          />&nbsp;
                          <input
                            onClick={(e) => e.stopPropagation()}
                            className="w-full border-none outline-none ring-0"
                            placeholder={t.searchFilters || "Buscar filtros"}
                            type="text"
                            data-filtername={filterTypes[s]}
                            ref={searchRef}
                            onChange={(e) => handleSearch(e)}
                          />
                        </div>
                      </div>
                    </Listbox.Option>

                    {searchList.length === 0 &&
                      groupByCategory[groupDictionary[s]]?.map((filter) => (
                        <Listbox.Option
                          as="div"
                          className="py-2 sm:px-4"
                          key={filter.name}
                          value={filter}
                        >
                          {({ selected }) => (
                            <CheckBox
                              key={filter.name}
                              name={filter.name}
                              checked={
                                selected ||
                                (query?.egira?.includes(filter.name) &&
                                  filter.uid === `${filter.key}EGIRA`) ||
                                query?.secciones?.includes(filter.name) ||
                                (query?.years?.includes(filter.key) &&
                                  filter.uid === `${filter.key}Years`) ||
                                query?.pais?.includes(filter.name)
                              }
                            />
                          )}
                        </Listbox.Option>
                      ))}

                    {searchList.length > 0 &&
                      searchList.map((filter) => (
                        <Listbox.Option
                          as="div"
                          className="py-2 sm:px-4"
                          key={filter.name}
                          value={filter}
                        >
                          {({ selected }) => (
                            <CheckBox
                              key={filter.name}
                              name={filter.name}
                              checked={selected}
                            />
                          )}
                        </Listbox.Option>
                      ))}

                    <div className="sticky bottom-0 flex w-full flex-row items-center justify-between border-t-2 bg-white pt-2 sm:px-4 sm:pb-2">
                      <button
                        onClick={() => {
                          setSelected([]);
                        }}
                        className="flex items-end justify-end border-b-2 border-neutral-400 font-semibold text-neutral-1000"
                      >
                        {t.clear || "Limpiar"}
                      </button>

                      <Button
                        onClick={() => window.location.reload()}
                        label={t.applyFilters || "Aplicar filtros"}
                        color="secondary-light"
                        className="bg-black font-semibold text-white"
                      />
                    </div>
                  </Listbox.Options>
                </Transition>
              </>
            )}
          </Listbox>
        );
      })}
    </>
  );
}

export const MobileFilter = ({
  themeFilters = [],
  onThemeChange = () => { },
  categoriesFilters = [],
  onCategoryChange = () => { },
  selectedCategories,
}) => {
  const [isShowing, setIsShowing] = React.useState(false);
  const [categorySearchList, setCategorySearchList] = React.useState([]);
  const [themeSearchList, setThemeSearchList] = React.useState([]);
  const [searchList, setSearchList] = useState([]);
  const [searchText, setSearchText] = React.useState("");
  const filters = useContext(FilterContext);
  const searchRef = useRef(null);

  const [selected, setSelected] = useState([]);
  const previousSelection = usePrevious(selected);

  const router = useRouter();
  const query = router.query;

  const [selectedThemes, setSelectedThemes] = useState([]);
  const [locale, setLocale] = useState("es-MX");
  const t = localeTexts[locale]?.common || {};

  const initiativesMap = useMemo(() => [
    {
      name: t.allPrograms || "Todos los programas",
      slug: "todas-las-iniciativas",
    },
    {
      name: t.ambulantePresenta || "Ambulante Presenta",
      slug: "ambulante-presenta",
    },
    {
      name: t.documentaryTour || "Gira de Documentales",
      slug: "gira-ambulante",
    },
    {
      name: t.ambulanteBeyond || "Ambulante Más Allá",
      slug: "ambulante-mas-alla",
    },
  ], [t]);

  const initialValue = query.iniciativa
    ? initiativesMap.filter((i) => i.slug === query.iniciativa)[0]
    : {};

  const [selectedInitiative, setSelectedInitiative] = useState(initialValue);
  const [filterType, setFilterType] = useState("todas-las-iniciativas");

  useEffect(() => {
    if (filters.length) {
      const themes = filters
        .filter((item) => item.category === "Temas" && item.isAdded)
        .map((item) => item.id);
      setSelectedThemes(themes);
    }
  }, [filters]);

  const groupByCategory = useMemo(() => {
    const res = filters.reduce((acc, item) => {
      const { category } = item;
      // if no item in category create empty array
      if (!acc[category]) {
        acc[category] = [];
      }
      // if category exists push item
      acc[category].push(item);
      return acc;
    }, {});
    return res;
  }, [filters]);

  useEffect(() => {
    if (selected.length > 0) {
      let filter = {};
      // create filter object
      selected.forEach((item) => {
        if (filter[item.category.toLocaleLowerCase()]) {
          filter[item.category.toLocaleLowerCase()].push(item.name);
        } else {
          filter[item.category.toLocaleLowerCase()] = [item.name];
        }
      });

      router.push({
        pathname: "/catalogo",
        query: {
          ...filter,
        },
      });
    } else {
      if (previousSelection?.length > 0) {
        router.push({
          pathname: "/catalogo",
        });
      }
    }
  }, [selected]);

  // const filterCollection = {
  //   "todas-las-iniciativas": {
  //     Secciones: groupByCategory["Secciones"],
  //     "Año de producción": groupByCategory["Years"],
  //     País: groupByCategory["Pais"],
  //   },
  //   "gira-ambulante": {
  //     "Ediciones Gira": groupByCategory["EGIRA"],
  //     Secciones: groupByCategory["Secciones"],
  //     "Año de producción": groupByCategory["Years"],
  //     País: groupByCategory["Pais"],
  //   },
  //   "ambulante-mas-alla": {
  //     Generación: groupByCategory["Generaciones"],
  //     "Año de producción": groupByCategory["Years"],
  //   },
  //   "ambulante-presenta": {
  //     "Ediciones AP": groupByCategory["EAP"],
  //     "Año de producción": groupByCategory["Years"],
  //     País: groupByCategory["Pais"],
  //   },
  // };
const filterCollection = useMemo(() => {
  const categories = {
    sections: t.sections || "Secciones",
    productionYear: t.productionYear || "Año de producción", 
    country: t.country || "País",
    tourEditions: t.tourEditions || "Ediciones Gira",
    generations: t.generations || "Generación",
    apEditions: t.apEditions || "Ediciones AP",
  };

  return {
    "todas-las-iniciativas": {
      [categories.sections]: groupByCategory["Secciones"],
      [categories.productionYear]: groupByCategory["Years"],
      [categories.country]: groupByCategory["Pais"],
    },
    "gira-ambulante": {
      [categories.tourEditions]: groupByCategory["EGIRA"],
      [categories.sections]: groupByCategory["Secciones"],
      [categories.productionYear]: groupByCategory["Years"],
      [categories.country]: groupByCategory["Pais"],
    },
    "ambulante-mas-alla": {
      [categories.generations]: groupByCategory["Generaciones"],
      [categories.productionYear]: groupByCategory["Years"],
    },
    "ambulante-presenta": {
      [categories.apEditions]: groupByCategory["EAP"],
      [categories.productionYear]: groupByCategory["Years"],
      [categories.country]: groupByCategory["Pais"],
    },
  };
}, [t, groupByCategory]);

  useEffect(() => {
    if (query?.iniciativa) {
      setFilterType(query.iniciativa);
    }
  }, [query.iniciativa]);

  useEffect(() => {
    if (
      selectedInitiative.slug &&
      query?.iniciativa != selectedInitiative.slug
    ) {
      setSelected([]);
      router.push({
        pathname: "/catalogo",
        query: { iniciativa: selectedInitiative.slug },
      });
    }
  }, [selectedInitiative]);

  const handleSearch = (e) => {
    const value = e;
    if (value === "") {
      setThemeSearchList(themeFilters);
      setCategorySearchList(categoriesFilters);
    } else {
      setThemeSearchList(
        themeFilters.filter((filter) =>
          filter.name.toLowerCase().includes(value.toLowerCase())
        )
      );
      setCategorySearchList(
        categoriesFilters.filter((filter) =>
          filter.name.toLowerCase().includes(value.toLowerCase())
        )
      );
    }
  };

  React.useEffect(() => {
    handleSearch(searchText);
  }, [searchText]);


  const filterTypes = useMemo(() => ({
    [t.themes]: "Temas",
    [t.productionYear]: "Years",
    [t.country]: "Pais",
    [t.sections]: "Secciones",
    [t.apEditions]: "EAP",
    [t.tourEditions]: "EGIRA",
    [t.generation]: "Generaciones",
    [t.publicationType]: "Tipo"
  }), [t]);
  return (
    <>
      <button
        onClick={() => setIsShowing(!isShowing)}
        className="z- left-0 flex h-full flex-col items-center justify-center active:bg-slate-100 "
      >
        <AdjustmentsIcon className=" h-6 w-6 text-neutral-900" />
      </button>

      <ClientOnlyPortal selector="#documentRoot">
        {isShowing && (
          <div
            onClick={() => setIsShowing(false)}
            className="fixed left-0 top-0 z-40 h-screen w-screen bg-neutral-900/50"
          ></div>
        )}
        <Transition as="div" show={isShowing}>
          <Transition.Child
            as="div"
            className="isShowing fixed bottom-0  z-50 h-screen-80 w-full overflow-scroll rounded-lg bg-white shadow-inner"
            enter="transition ease duration-300 transform"
            enterFrom="opacity-0 translate-y-12"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease duration-300 transform"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-12"
          >
            <div className="amb-container m-auto mt-5 space-y-5">
              <div className="flex items-center  ">
                <div className="flex h-full items-center gap-3">
                  <Text.Body as="p" size="xl">
                    &nbsp;
                    {t.filters || "Filtros"}
                  </Text.Body>
                </div>
                <div className="flex-grow"></div>
                <div>
                  <XIcon
                    onClick={() => setIsShowing(!isShowing)}
                    className="h-6 w-6 text-neutral-900"
                  />
                </div>
              </div>
            </div>
            <div>
              <div className="mt-3 bg-neutral-50 ">
                <div className="amb-container py-3">{t.programs || "Programas"}</div>
              </div>
            </div>

            <div className="amb-container">
              <RadioGroup
                value={selectedInitiative}
                className="grid w-full divide-y-2 md:grid-cols-2"
                onChange={setSelectedInitiative}
              >
                {initiativesMap.map((initiative) => (
                  <div
                    key={initiative.slug}
                    className="flex w-full items-center"
                  >
                    <RadioGroup.Option
                      value={initiative}
                      className="flex w-full px-3 py-3 focus:ring-black"
                    >
                      {({ checked, active }) => (
                        <div className="flex w-full items-center">
                          <input
                            id={initiative.slug}
                            name="notification-method"
                            type="radio"
                            checked={
                              checked ||
                              query?.iniciativa?.includes(initiative.slug)
                            }
                            className="h-4 w-4 border-gray-300  pr-2 text-black focus:ring-black"
                          />
                          <div className="flex-grow"></div>
                          <RadioGroup.Label
                            htmlFor={initiative.slug}
                            className="block w-full pl-2 text-sm font-medium leading-6 text-gray-900"
                          >
                            <Text.Label> {initiative.name}</Text.Label>
                          </RadioGroup.Label>
                        </div>
                      )}
                    </RadioGroup.Option>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="amb-container">
              <RadioGroup
                as="div"
                className=" relative"
                value={selectedThemes}
                onChange={setSelectedThemes}
                multiple
              >
                {themeFilters.map((list) => (
                  <RadioGroup.Option
                    as="div"
                    className="py-2 sm:px-4"
                    key={list.name}
                    value={list.value}
                  >
                    {({ selected: s }) => (
                      <CheckBox key={list.name} name={list.name} checked={s} />
                    )}
                  </RadioGroup.Option>
                ))}
              </RadioGroup>
            </div>

            {Object.entries(filterCollection[filterType]).map(
              ([s, x], index) => {
                return (
                  <div key={index}>
                    <div>
                      <div className="mt-3 bg-neutral-50 ">
                        <div className="amb-container py-3 capitalize">{Object.keys(filterTypes).find(key => 
                          filterTypes[key] === groupDictionary[s]
                        ) || s}</div>
                      </div>
                    </div>

                    <div className="amb-container relative grid grid-cols-2">
                      {searchList.length === 0 ? (
                        groupByCategory[groupDictionary[s]]?.map((filter) => (
                          <div
                            key={filter.name}
                            onClick={() => {
                              if (
                                selected.filter(
                                  (item) => item.name !== filter.name
                                )
                              ) {
                                setSelected([...selected, filter]);
                              } else {
                                setSelected(
                                  selected.filter(
                                    (item) => item.name !== filter.name
                                  )
                                );
                              }
                            }}
                          >
                            <CheckBox
                              name={filter.name}
                              checked={
                                selected.includes(filter) ||
                                (query?.egira?.includes(filter?.name) &&
                                  filter.uid === `${filter.key}EGIRA`) ||
                                query?.secciones?.includes(filter?.name) ||
                                (query?.years?.includes(filter?.key) &&
                                  filter.uid === `${filter.key}Years`) ||
                                query?.pais?.includes(filter?.name)
                              }
                            />
                          </div>
                        ))
                      ) : (
                        <pre>{JSON.stringify(searchList, null, 2)}</pre>
                      )}
                    </div>
                  </div>
                );
              }
            )}

            <div className="sticky bottom-0 flex w-full flex-row items-center justify-between border-t-2 bg-white pt-2 sm:px-4 sm:pb-2">
              <button
                onClick={() => {
                  setIsShowing(false);
                  setSelectedThemes([]);
                  setSelected([]);
                }}
                className="flex items-end justify-end border-b-2 border-neutral-400 font-semibold text-neutral-1000"
              >
                {t.clear || "Limpiar"}
              </button>

              <Button
                onClick={() => {
                  setIsShowing(false);
                }}
                label={t.applyFilters || "Aplicar filtros"}
                color="secondary-light"
                className="bg-black font-semibold text-white"
              />
            </div>
          </Transition.Child>
        </Transition>
      </ClientOnlyPortal>
    </>
  );
};

Filter.propTypes = {
  type: PropTypes.string,
  filterType: PropTypes.oneOf(["filters", "initiatives"]).isRequired,
};
